import { Box, Modal, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useEffect } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import CircleOption from "../../../../../components/filterOption/CircleOption";
import ImgOption from "../../../../../components/filterOption/SeverityOption";
import FilterMenu from "../../../../../components/filters/FilterMenu";
import SearchFilter from "../../../../../components/filters/SearchFilter";
import SearchInput from "../../../../../components/filters/SearchInput";
import { ControlStatus } from "../../../../../utils/constants";
import IntegrationRowFilter from "../../../../posture/steps/components/IntegrationRowFilter";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";
import RemediationPerformanceTable from "./RemediationPerformanceTable";

import { useGridApiRef } from "@mui/x-data-grid-pro";
import AssigneeLabel from "../../../../../components/assignee/AssigneeLabel";
import CustomTooltip from "../../../../../components/CustomTooltip";
import ClearFiltersButton from "../../../../../components/filters/ClearFiltersButton";
import { useFileNaming } from "../../../../../hooks/useFileNaming";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { controlSeverity } from "../../../../../utils/constants";
import { searchObjectProperties } from "../../../../../utils/search";
import { icons } from "../../../../stack/environment/icons/iconExport";

const RemediationPerformanceModal = ({
  showActivityLogTable,
  setShowActivityLogTable,
}) => {
  const colors = useColors();
  const { getActivityLogImproved, trackEvent } = useAPI();
  const gridRef = useGridApiRef();
  const { generateFileName } = useFileNaming();
  const [filteredActivityLogImproved, setFilteredActivityLogImproved] =
    useState([]);
  const [appliedFilters, setAppliedFilters] = useState({
    tools: {},
    severities: {},
    controlStatuses: {},
    assignee: {},
    searchString: "",
  });
  const [filterOption, setFilterOptions] = useState({
    tools: [],
    severities: ["High", "Medium", "Low"],
    controlStatuses: [],
    assignee: [],
  });

  const { data: activityLogImproved, isLoading } = useQuery({
    queryKey: ["activityLogImproved"],
    queryFn: () => getActivityLogImproved(),
    select: (data) => data?.data || data || [],
  });

  useEffect(() => {
    if (activityLogImproved) {
      let assignee = [];
      for (const log of activityLogImproved) {
        if (
          log.assignee &&
          !assignee.find((a) => a.email === log.assignee.email)
        ) {
          assignee.push(log.assignee);
        }
      }
      setFilterOptions({
        tools: ["All", ...new Set(activityLogImproved.map((log) => log.tool))],
        severities: ["High", "Medium", "Low"],
        controlStatuses: Object.values(ControlStatus),
        assignee: ["All", "None", ...new Set(assignee)],
      });
      setFilteredActivityLogImproved(activityLogImproved);
    }
  }, [activityLogImproved]);

  const doesActivityLogFilteredAssignee = (log) => {
    const assignee = Object.keys(appliedFilters.assignee).filter(
      (key) => appliedFilters.assignee[key] === true
    );
    if (assignee.length === 0) {
      return true;
    }
    if (assignee.includes("None") && !log.assignee_email) {
      return true;
    } else return assignee.includes(log.assignee_email);
  };

  useEffect(() => {
    trackEvent(`user change filters to ${JSON.stringify(appliedFilters)}`);

    if (activityLogImproved && activityLogImproved) {
      let filteredRows = activityLogImproved;

      if (Object.keys(appliedFilters.tools)?.length > 0) {
        const tools_filter = Object.keys(appliedFilters.tools).filter(
          (key) => appliedFilters.tools[key] === true
        );
        if (tools_filter.length > 0) {
          filteredRows = filteredRows.filter((log) =>
            tools_filter.includes(log.tool)
          );
        }
      }

      if (Object.keys(appliedFilters?.controlStatuses)?.length > 0) {
        const filterStatuses = Object.keys(
          appliedFilters.controlStatuses
        ).filter((key) => appliedFilters.controlStatuses[key] === true);
        if (filterStatuses.length > 0) {
          filteredRows = filteredRows.filter((log) => {
            return filterStatuses.includes(log.status);
          });
        }
      }

      if (Object.keys(appliedFilters.severities)?.length > 0) {
        const filterSeverities = Object.keys(appliedFilters.severities).filter(
          (key) => appliedFilters.severities[key] === true
        );
        if (filterSeverities.length > 0) {
          filteredRows = filteredRows.filter((row) =>
            filterSeverities.includes(controlSeverity[row.control_severity])
          );
        }
      }

      if (Object.keys(appliedFilters.assignee)?.length > 0) {
        filteredRows = filteredRows.filter((control) =>
          doesActivityLogFilteredAssignee(control)
        );
      }

      if (appliedFilters.searchString !== "") {
        const searchString = appliedFilters.searchString;
        const parsedNumber = Number(searchString);
        const isNumber = !isNaN(parsedNumber);
        filteredRows = filteredRows.filter((row) => {
          if (isNumber) {
            return row.id && row.id.toString().includes(searchString);
          } else {
            return searchObjectProperties(row, searchString.toLowerCase());
          }
        });
      }
      setFilteredActivityLogImproved(filteredRows);
    }
  }, [appliedFilters]);

  const handleTableExport = () => {
    const message =
      "User click on 'Export' button to download the remediation performance table";
    trackEvent(message);
    const csvExportOptions = {
      fileName: generateFileName(`remediation_performance`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  return (
    <Modal
      open={showActivityLogTable}
      onClose={() => setShowActivityLogTable(false)}
    >
      <Box
        padding="24px"
        position="absolute"
        borderRadius="10px"
        top="50%"
        left="50%"
        sx={{
          transform: "translate(-50%, -50%)",
          backgroundColor: colors.modal_bg,
          outline: "none",
          display: "flex",
          flexDirection: "column",
          width: "90%",
          height: "90%",
          gap: 2,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          paddingBottom={2}
        >
          <Typography variant="h6">Remediation Performance</Typography>

          <Box onClick={() => setShowActivityLogTable(false)}>
            <SvgToIcon
              IconLogo={icons.CloseIcon}
              style={{ cursor: "pointer", width: "20px", height: "20px" }}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Box display="flex" flexDirection="row" gap={2} width="90%">
            <Box display="flex" flexDirection="row" width="25%">
              <SearchInput
                fieldName={"searchString"}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Box>
            {/* TOOL FILTER */}
            <SearchFilter
              filterName={"Tool"}
              fieldName={"tools"}
              options={filterOption.tools.map((tool) => {
                if (tool === "All") {
                  return {
                    name: tool,
                    label: (
                      <Typography
                        style={{
                          textTransform: "none",
                          fontSize: "12px",
                          paddingLeft: "8px",
                        }}
                      >
                        All Tools
                      </Typography>
                    ),
                    displayName: tool,
                  };
                } else
                  return {
                    name: tool,
                    label: <IntegrationRowFilter name={tool} />,
                    displayName: IntegrationsMapping[tool]?.displayName,
                  };
              })}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              fullOptions={filterOption.tools}
            />
            {/* SEVERITY FILTER */}
            <FilterMenu
              filterName={"Severity"}
              fieldName={"severities"}
              options={filterOption.severities.map((severity) => {
                return {
                  name: severity,
                  label: <ImgOption type={"severity"} label={severity} />,
                  displayName: severity,
                };
              })}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              fullOptions={filterOption.severities}
            />
            {/* CONTROL STATUS FILTER */}
            <FilterMenu
              filterName={"Status"}
              fieldName={"controlStatuses"}
              options={filterOption.controlStatuses.map((status) => {
                return {
                  name: status,
                  label: <CircleOption label={status} type={"status"} />,
                  displayName: status,
                };
              })}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              fullOptions={filterOption.controlStatuses}
              displayAs={"chip"}
            />
            {/* ASSIGNEE FILTER */}
            <SearchFilter
              filterName={"Assignee"}
              fieldName={"assignee"}
              options={filterOption.assignee.map((assignee) => {
                if (assignee === "None") {
                  return {
                    name: assignee,
                    label: (
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        <SvgToIcon
                          IconLogo={icons.AssigneeIcon}
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Typography variant="caption">No Assignee</Typography>
                      </Box>
                    ),
                    displayName: "No Assignee",
                  };
                }
                if (assignee === "All") {
                  return {
                    name: assignee,
                    label: (
                      <Typography variant="caption">All Options</Typography>
                    ),
                    displayName: assignee,
                  };
                }
                return {
                  name: assignee?.email,
                  label: <AssigneeLabel assignee={assignee} />,
                  displayName: assignee?.display_name,
                };
              })}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              fullOptions={filterOption.assignee}
            />
            <ClearFiltersButton
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Box>
          <CustomTooltip title="Export to CSV" placement="top">
            <Box
              onClick={handleTableExport}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "7px",
                width: "32px",
                height: "32px",
                border: `1px solid ${colors.enabled_stroke}`,
                borderRadius: "8px",
              }}
            >
              <SvgToIcon
                IconLogo={icons.ExportCSVIcon}
                style={{ width: "18px", height: "18px" }}
              />
            </Box>
          </CustomTooltip>
        </Box>
        <RemediationPerformanceTable
          activityLog={filteredActivityLogImproved}
          isLoading={isLoading}
          refToPass={gridRef}
        />
      </Box>
    </Modal>
  );
};

export default RemediationPerformanceModal;
